import type { CodeoBrand } from 'js/src/client';
import { type PlanManifest } from 'js/src/agent/codeo/plan-manifest';
import { BLANK_COMPOSITION } from 'js/src/agent/codeo/starterCompositions';
import type { WebsiteAnalysisLogEntry } from 'js/src/agent/codeo/website-brand/types';
import type {
    PendingReferenceVideo,
    PendingWebsite,
    ReferenceAnalysisLogEntry,
    SelectedElement,
} from 'js/src/data/codeo/codeoSlice';
import type { RootState } from 'js/src/libs/RootState';

export const selectCurrentHtml = (state: RootState) => state.codeo.currentHtml;

export const selectDomEditSelection = (state: RootState): SelectedElement | null =>
    state.codeo.domEditSelection;

export const selectChatReferenceAsset = (state: RootState): SelectedElement | null =>
    state.codeo.chatReferenceAsset;

export const selectAgentRevision = (state: RootState): number => state.codeo.agentRevision;

export const selectIsAgentProcessing = (state: RootState): boolean => state.codeo.isAgentProcessing;

export const selectCommittedPlan = (state: RootState): PlanManifest | null =>
    state.codeo.committedPlan;

export const selectPendingReference = (state: RootState): PendingReferenceVideo | null =>
    state.codeo.pendingReference;

export const selectLastReferenceAnalysis = (state: RootState): ReferenceAnalysisLogEntry | null =>
    state.codeo.lastReferenceAnalysis;

export const selectPendingWebsite = (state: RootState): PendingWebsite | null =>
    state.codeo.pendingWebsite;

export const selectLastWebsiteAnalysis = (state: RootState): WebsiteAnalysisLogEntry | null =>
    state.codeo.lastWebsiteAnalysis;

export const selectQueuedInitialMessage = (state: RootState): string | null =>
    state.codeo.queuedInitialMessage;

export const selectCodeoAssets = (state: RootState): Record<string, string> =>
    state.codeo.assets ?? {};

/**
 * True when the composition is the blank starter placeholder (no real video
 * yet). Used to disable the asset-picker UI — there's nothing meaningful to
 * pick from until the agent has generated something.
 */
export const selectHasComposition = (state: RootState) =>
    selectCurrentHtml(state) !== BLANK_COMPOSITION;

export const selectCodeoBrands = (state: RootState): CodeoBrand[] => state.codeo.brands;

export const selectSelectedBrandId = (state: RootState): number | null =>
    state.codeo.selectedBrandId;

export const selectIsLoadingBrands = (state: RootState): boolean => state.codeo.isLoadingBrands;

export const selectSelectedBrand = (state: RootState): CodeoBrand | null =>
    state.codeo.brands.find((brand) => brand.id === state.codeo.selectedBrandId) ?? null;
